<template>
  <div class="pptedit-raster">
    <!-- 顶部工具条 -->
    <div class="edit-bar">
      <el-button size="small" icon="el-icon-back" @click="goBack">返回列表</el-button>
      <span class="bar-title">{{ title || '未命名PPT' }}</span>
      <span class="bar-info">共 {{ pages.length }} 页 · 整页图替换编辑（失真页用截图替换，其余保持渲染图）</span>
      <div class="bar-actions">
        <el-button size="small" type="primary" plain icon="el-icon-video-camera" :disabled="pages.length === 0" @click="recapVisible = true">补拍动画帧</el-button>
        <el-button size="small" type="primary" :loading="saving" @click="save">保存</el-button>
      </div>
    </div>

    <div class="edit-body">
      <!-- 左侧：页面缩略图列表 -->
      <div class="thumbs" v-loading="loading">
        <div
          v-for="(p, i) in pages"
          :key="p.id"
          class="thumb-item"
          :class="{ on: i === cur }"
          @click="selectPage(i)"
        >
          <img :src="pageThumb(p)" :alt="'第' + (i + 1) + '页'" loading="lazy">
          <div class="thumb-no">{{ i + 1 }}</div>
          <div v-if="p._blank" class="thumb-tag blank">空白</div>
          <div v-else-if="p._replaced" class="thumb-tag">已替换</div>
          <button class="thumb-del" title="删除本页" @click.stop="delPage(i)">✕</button>
        </div>
        <div v-if="!loading && pages.length === 0" class="empty">无页面数据</div>
      </div>

      <!-- 右侧：当前页大图 + 操作 -->
      <div class="stage" v-if="pages.length > 0" v-loading="loading">
        <div class="stage-head">
          <span class="page-label">第 {{ cur + 1 }} / {{ pages.length }} 页</span>
          <span v-if="current._replaced" class="replaced-tip">★ 本页已用截图替换</span>
        </div>

        <div class="stage-body">
          <div class="canvas-wrap" :style="canvasStyle">
            <img :src="currentImg" class="page-img" alt="当前页">
          </div>
        </div>

        <div class="stage-tools">
          <el-button v-if="isAnimPage" size="small" icon="el-icon-video-play" @click="openAnimPreview">预览动画效果</el-button>
          <el-button size="small" type="warning" :loading="uploading" icon="el-icon-picture-outline" @click="openPicker">上传截图替换本页</el-button>
          <el-button size="small" type="danger" :loading="videoUploading" icon="el-icon-upload2" @click="confirmVideoReplace">上传动画替换本页</el-button>
          <el-button size="small" icon="el-icon-circle-plus-outline" @click="insertPage(-1)">在前加一页</el-button>
          <el-button size="small" icon="el-icon-circle-plus-outline" @click="insertPage(1)">在后加一页</el-button>
          <el-button
            v-if="current._replaced"
            size="small"
            icon="el-icon-refresh-left"
            @click="restorePage"
          >恢复为渲染图</el-button>
          <span class="tool-tip">{{ toolTipText }}</span>
          <input ref="videoInput" type="file" accept="video/mp4" style="display: none" @change="onVideoReplace">
        </div>
      </div>
    </div>

    <!-- 动画效果预览：逐帧播放该动画页的关键帧序列（与检查页/导出 HTML 行为一致） -->
    <el-dialog title="动画效果预览" :visible.sync="animVisible" width="860px" top="4vh" @closed="stopAnimAuto">
      <div class="anim-pv">
        <div class="anim-stage">
          <img :src="animFrameSrc" alt="动画帧">
        </div>
        <div class="anim-ctrl">
          <el-button size="small" :disabled="animStep <= 0" @click="animPrev">‹ 上一步</el-button>
          <span class="anim-progress">第 {{ animStep }} 步 / 共 {{ animTotal }} 步动画（与 PowerPoint 放映一致，点「下一步」播下一帧）</span>
          <el-button size="small" :disabled="animStep >= animTotal" @click="animNext">下一步 ›</el-button>
          <el-button size="small" type="primary" :icon="animPlaying ? 'el-icon-video-pause' : 'el-icon-video-play'" @click="toggleAnimAuto">
            {{ animPlaying ? '暂停' : '自动播放' }}
          </el-button>
        </div>
      </div>
    </el-dialog>

    <!-- 画面来源选择：原渲染图 / 动画关键帧 / 本地图片上传（标题与说明随页型） -->
    <el-dialog :title="pickerTitle" :visible.sync="pickerVisible" width="760px" top="6vh">
      <div class="picker-tip">{{ pickerDesc }}</div>
      <div class="picker-grid" v-loading="loading">
        <div
          v-for="(opt, i) in pickerOptions"
          :key="i"
          class="picker-item"
          title="点击使用这张画面"
          @click="applyPicker(i)"
        >
          <img :src="opt.src" alt="">
          <div class="picker-label">{{ opt.label }}</div>
        </div>
        <el-upload
          :show-file-list="false"
          :http-request="uploadReplace"
          accept="image/png,image/jpeg,image/jpg"
          class="picker-item picker-upload"
        >
          <span class="picker-upload-plus">＋ 本地图片…</span>
          <span class="picker-upload-sub">(真实环境弹出文件选择框)</span>
        </el-upload>
      </div>
    </el-dialog>

    <!-- 补拍动画帧向导：真屏连拍+内录音，替换交互页等自动转换缺失的动画（commit 即时落库，本地数据同步后随「保存」一致回写） -->
    <recapture-dialog
      v-model="recapVisible"
      :project-id="id"
      :total="pages.length"
      :current-page="cur + 1"
      @applied="applyRecaptureResult"
    />
  </div>
</template>

<script>
import {
  getPptProjectById,
  updatePptProject,
  uploadPptImage,
  uploadPptVideo
} from '@/api/ppt'
import RecaptureDialog from './recapture-dialog.vue'

// 新增空白页的占位图（SVG 内联 data URI，随 pagesJson 保存，导出/预览均无需额外资源）
const BLANK_IMG = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(
  '<svg xmlns="http://www.w3.org/2000/svg" width="960" height="540">' +
  '<rect width="960" height="540" fill="#e8eaed"/>' +
  '<text x="480" y="270" font-size="40" fill="#909399" text-anchor="middle" dominant-baseline="middle">空白页（请上传截图或动画）</text>' +
  '</svg>'
)

// 空白页上传视频时截帧失败的中性封面：不再出现"请上传…"类操作提示文字
const VIDEO_BLANK_IMG = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(
  '<svg xmlns="http://www.w3.org/2000/svg" width="960" height="540">' +
  '<rect width="960" height="540" fill="#2b2f36"/>' +
  '<circle cx="480" cy="250" r="46" fill="none" stroke="#ffffff" stroke-width="6" opacity="0.85"/>' +
  '<path d="M466 224 L508 250 L466 276 Z" fill="#ffffff" opacity="0.85"/>' +
  '<text x="480" y="360" font-size="36" fill="#c0c4cc" text-anchor="middle" dominant-baseline="middle">视频页</text>' +
  '</svg>'
)

export default {
  name: 'PptEditRaster',
  components: { RecaptureDialog },
  data() {
    return {
      id: null,
      title: '',
      themeJson: null,
      pages: [],      // [{ id, img, title, type, frames, frameT, anim, audio, audioT1, videoUrl, videoBox, _origImg, _replaced }]
      cur: 0,
      loading: false,
      saving: false,
      uploading: false,
      videoUploading: false,
      pickerVisible: false,
      pickerOptions: [],
      recapVisible: false,
      // 动画预览
      animVisible: false,
      animStep: 0,
      animPlaying: false,
      animTimer: null
    }
  },
  computed: {
    current() {
      return this.pages[this.cur] || {}
    },
    // 当前页展示图：动画页（sequence）显示首帧（初始态，与检查页第一眼一致）；已用截图替换则显示替换图
    currentImg() {
      const p = this.current
      if (!p) return ''
      if (p._replaced) return p.img
      if (p.type === 'sequence' && p.frames && p.frames.length) return p.frames[0]
      return p.img
    },
    // 替换按钮已拆分为「上传截图替换」「上传动画替换」两个常驻按钮，任何页型都可用两种方式替换
    toolTipText() {
      return '提示：每页都支持两种方式替换——「上传截图」换整页画面（视频/动画页换封面），「上传动画」传 mp4 整页播放；「在前/在后加一页」插入空白页后用同样方式填充。单击交互等自动转换出不来动画的页，点顶部「补拍动画帧」在 PowerPoint 里手动放映补拍真帧。'
    },
    // 截图选择器：仅普通页使用（动画页替换=上传视频；视频页替换=上传视频）
    pickerTitle() {
      return '选择本页截图'
    },
    pickerDesc() {
      return '用 PowerPoint 打开原 .pptx → 对该页截图（Win+Shift+S / 截图工具）→ 保存 → 点击下方「本地图片」上传即替换本页。动画页还可直接选用自动截取的关键帧。'
    },
    // 当前页是否为动画页（有 2 帧以上才可预览）
    isAnimPage() {
      const p = this.current
      return !!(p.type === 'sequence' && p.frames && p.frames.length >= 2)
    },
    // 动画预览帧数组（首帧 = 初始态）
    animFrames() {
      const p = this.current
      return (p.type === 'sequence' && Array.isArray(p.frames) && p.frames.length) ? p.frames : []
    },
    animTotal() {
      return Math.max(0, this.animFrames.length - 1)
    },
    animFrameSrc() {
      return this.animFrames[this.animStep] || ''
    },
    canvasStyle() {
      // 按画布比例（960:540 或 PPT 原始比例）约束展示区域
      let w = 960
      let h = 540
      try {
        if (this.themeJson) {
          const t = JSON.parse(this.themeJson)
          if (t.pageW && t.pageH) {
            w = Number(t.pageW)
            h = Number(t.pageH)
          }
        }
      } catch (e) { /* 忽略 */ }
      const ratio = w / h
      return { aspectRatio: String(ratio) }
    }
  },
  created() {
    this.id = this.$route.query && this.$route.query.id
    if (!this.id) {
      this.$message.warning('缺少项目 ID')
      return
    }
    this.loadDetail()
  },
  methods: {
    loadDetail() {
      this.loading = true
      getPptProjectById(this.id)
        .then((res) => {
          const d = (res && res.data && res.data.data) || null
          if (!d) {
            this.$message.error('未找到 PPT 项目')
            return
          }
          this.title = d.title
          this.themeJson = d.themeJson
          let list = []
          try {
            list = JSON.parse(d.pagesJson || '[]')
          } catch (e) {
            list = []
          }
          // 兼容两种结构：整页图 {id,img,title} / 旧元素结构（无 img 时按封面占位）
          // 保留页型（type/frames/videoUrl/videoBox）：动画页可选关键帧替换、视频页保留原位播放，保存时不丢失
          this.pages = list.map((p) => {
            const img = p.img || p.cover || ''
            return {
              ...p, // 原样保留编辑器不认识的字段（audios/videos/audioMedia/audioBox/audioLong/audioStart/audioEnd），编辑保存不再丢音频/视频对象
              id: p.id,
              img,
              title: p.title || '',
              type: p.type || 'image',
              frames: ((p.type === 'sequence' || p.type === 'hybrid') && Array.isArray(p.frames)) ? p.frames : null,
              frameT: ((p.type === 'sequence' || p.type === 'hybrid') && Array.isArray(p.frameT)) ? p.frameT : null,
              anim: !!p.anim,
              audio: (p.audio !== undefined && p.audio !== null) ? p.audio : null,
              audioT1: (p.audioT1 !== undefined && p.audioT1 !== null) ? p.audioT1 : null,
              videoUrl: p.videoUrl || '',
              videoBox: p.videoBox || null,
              _origImg: img,
              _replaced: false
            }
          })
          if (this.pages.length === 0) {
            this.$message.warning('该项目没有页面数据')
          }
        })
        .catch(() => {
          this.$message.error('加载失败')
        })
        .finally(() => {
          this.loading = false
        })
    },
    selectPage(i) {
      this.cur = i
    },
    // 缩略图展示图：动画页显示首帧（初始态），已替换则显示替换图
    pageThumb(p) {
      if (!p) return ''
      if (p._replaced) return p.img
      if (p.type === 'sequence' && p.frames && p.frames.length) return p.frames[0]
      return p.img
    },
    // 删除本页（如空白页/多余页），点「保存」后生效
    delPage(i) {
      this.$confirm('确认删除第 ' + (i + 1) + ' 页？删除后点「保存」生效。', '删除本页', { type: 'warning' })
        .then(() => {
          this.pages.splice(i, 1)
          if (this.cur >= this.pages.length) this.cur = Math.max(0, this.pages.length - 1)
          this.$message.success('已删除本页，点击「保存」生效')
        })
        .catch(() => {})
    },
    // 打开动画预览：从初始帧（第 0 步）开始
    openAnimPreview() {
      this.animStep = 0
      this.animVisible = true
    },
    animPrev() {
      if (this.animStep > 0) this.animStep--
    },
    animNext() {
      if (this.animStep < this.animTotal) this.animStep++
      if (this.animStep >= this.animTotal) this.stopAnimAuto()
    },
    toggleAnimAuto() {
      if (this.animPlaying) {
        this.stopAnimAuto()
        return
      }
      if (this.animStep >= this.animTotal) this.animStep = 0
      this.animPlaying = true
      this.animTimer = setInterval(() => {
        if (this.animStep >= this.animTotal) {
          this.stopAnimAuto()
          return
        }
        this.animStep++
      }, 900)
    },
    stopAnimAuto() {
      this.animPlaying = false
      if (this.animTimer) {
        clearInterval(this.animTimer)
        this.animTimer = null
      }
    },
    // 动画替换入口：任何页型都可上传 mp4，以整页视频播放替换本页（原关键帧序列丢弃）
    confirmVideoReplace() {
      const isAnim = this.current.type === 'sequence'
      this.$confirm(
        isAnim
          ? '该页为动画页：将上传 mp4 视频，以完整动画视频替代自动截取的关键帧序列（替换后本页为整页视频播放，封面用当前展示图）。\n确定继续吗？'
          : '将上传 mp4 视频替换本页（整页视频播放，封面用当前展示图）。\n确定继续吗？',
        '上传动画替换本页',
        { type: 'warning', confirmButtonText: '选择视频', cancelButtonText: '取消' }
      ).then(() => this.openVideoReplace()).catch(() => {})
    },
    openVideoReplace() {
      if (this.$refs.videoInput) this.$refs.videoInput.click()
    },
    onVideoReplace(e) {
      const file = e.target.files && e.target.files[0]
      e.target.value = ''
      if (!file) return
      if (file.type !== 'video/mp4' && !/\.mp4$/i.test(file.name)) {
        this.$message.warning('仅支持 mp4 视频')
        return
      }
      this.videoUploading = true
      uploadPptVideo(file)
        .then((res) => {
          if (res.data.code !== 0 || !res.data.data) {
            this.$message.error((res.data && res.data.message) || '上传失败')
            return
          }
          const p = this.current
          p.videoUrl = res.data.data
          // 统一转为整页视频页：丢弃关键帧序列/旧视频标记
          p.type = 'video'
          delete p.frames
          delete p._replaced
          delete p._blank
          // 空白页转视频时封面仍是"空白页"占位图（导出后播放前会露出提示文字）：
          // 自动从视频首帧截一张封面替换；截帧失败退回到中性"视频页"占位图
          if (p.img === BLANK_IMG) {
            this.applyVideoPoster(p, file)
          }
          this.$message.success('已上传动画视频替换本页（整页视频播放），点击「保存」生效')
        })
        .catch(() => this.$message.error('视频上传失败'))
        .finally(() => {
          this.videoUploading = false
        })
    },
    // 从本地视频文件截首帧（0.1s）作为封面：本地 canvas 截帧 → 上传 → 写入 p.img；失败用中性占位图
    applyVideoPoster(p, file) {
      this.makeVideoPoster(file).then((dataUrl) => {
        if (!dataUrl) {
          p.img = VIDEO_BLANK_IMG
          p._origImg = VIDEO_BLANK_IMG
          return
        }
        fetch(dataUrl)
          .then((r) => r.blob())
          .then((blob) => uploadPptImage(new File([blob], 'poster.jpg', { type: 'image/jpeg' })))
          .then((res) => {
            const url = this.extractOssUrl(res)
            if (url) {
              p.img = url
              p._origImg = url
            } else {
              p.img = VIDEO_BLANK_IMG
              p._origImg = VIDEO_BLANK_IMG
            }
          })
          .catch(() => {
            p.img = VIDEO_BLANK_IMG
            p._origImg = VIDEO_BLANK_IMG
          })
      })
    },
    makeVideoPoster(file) {
      return new Promise((resolve) => {
        let done = false
        let objUrl = ''
        let timer = null
        const finish = (v) => {
          if (done) return
          done = true
          if (timer) clearTimeout(timer)
          try { URL.revokeObjectURL(objUrl) } catch (e) { /* 忽略 */ }
          resolve(v)
        }
        try {
          objUrl = URL.createObjectURL(file)
          const v = document.createElement('video')
          v.muted = true
          v.playsInline = true
          v.preload = 'auto'
          v.src = objUrl
          timer = setTimeout(() => finish(null), 5000)
          v.addEventListener('loadeddata', () => { try { v.currentTime = 0.1 } catch (e) { finish(null) } })
          v.addEventListener('seeked', () => {
            try {
              const vw = v.videoWidth
              const vh = v.videoHeight
              if (!vw || !vh) { finish(null); return }
              const c = document.createElement('canvas')
              c.width = 960
              c.height = 540
              const ctx = c.getContext('2d')
              const scale = Math.max(960 / vw, 540 / vh)
              const dw = vw * scale
              const dh = vh * scale
              ctx.drawImage(v, (960 - dw) / 2, (540 - dh) / 2, dw, dh)
              finish(c.toDataURL('image/jpeg', 0.85))
            } catch (e) { finish(null) }
          })
          v.addEventListener('error', () => finish(null))
        } catch (e) { finish(null) }
      })
    },
    // 在当前页前/后插入空白页：占位图展示，用「上传截图/上传动画」填充内容，点「保存」生效
    insertPage(offset) {
      const blank = {
        id: 0,
        img: BLANK_IMG,
        title: '',
        type: 'image',
        _origImg: BLANK_IMG,
        _replaced: false,
        _blank: true
      }
      const at = offset > 0 ? this.cur + 1 : this.cur
      this.pages.splice(at, 0, blank)
      this.cur = at
      this.$message.success('已在当前页' + (offset > 0 ? '后' : '前') + '新增空白页，请上传截图或动画填充，点「保存」生效')
    },
    // 打开「截图来源选择」：动画页关键帧（本地图片走 uploadReplace）；原渲染图恢复由「恢复为渲染图」按钮承担，不在此列出
    openPicker() {
      const p = this.current
      const opts = []
      // 动画页：已自动截取的各步关键帧（第 0 帧即首帧，与当前展示相同，从第 1 步起列出）
      if (p.type === 'sequence' && p.frames && p.frames.length > 1) {
        p.frames.forEach((f, i) => {
          if (i === 0) return
          opts.push({ label: '动画第 ' + i + ' 步关键帧（自动截取）', src: f })
        })
      }
      this.pickerOptions = opts
      this.pickerVisible = true
    },
    // 点选某张截图 → 替换当前页
    applyPicker(i) {
      const opt = this.pickerOptions[i]
      if (!opt) return
      const p = this.pages[this.cur]
      p.img = opt.src
      p._replaced = opt.src !== p._origImg
      delete p._blank
      this.pickerVisible = false
      this.$message.success('已用「' + opt.label + '」替换本页，点击"保存"生效')
    },
    // 上传截图替换当前页
    uploadReplace(option) {
      const file = option.file
      if (!file) return
      this.uploading = true
      uploadPptImage(file)
        .then((res) => {
          const url = this.extractOssUrl(res)
          if (!url) {
            this.$message.error('上传失败：未获取到图片地址')
            return
          }
          const p = this.pages[this.cur]
          p.img = url
          p._replaced = true
          delete p._blank
          this.pickerVisible = false
          this.$message.success('已替换本页，点击"保存"生效')
        })
        .catch(() => {
          this.$message.error('上传失败，请重试')
        })
        .finally(() => {
          this.uploading = false
        })
    },
    // 从平台上传接口返回值中提取 OSS 直链
    extractOssUrl(res) {
      try {
        if (res && res.data) {
          if (typeof res.data === 'string') return res.data
          if (res.data.data) return String(res.data.data)
        }
        if (res && res.data && res.data.url) return String(res.data.url)
      } catch (e) { /* 忽略 */ }
      return null
    },
    restorePage() {
      const p = this.pages[this.cur]
      p.img = p._origImg
      p._replaced = false
      this.$message.success('已恢复为渲染图，点击"保存"生效')
    },
    // 补拍向导回写：第 page 页（1 起）commit 成功后同步本地数据（后端已即时落库，此处保证「保存」不会用旧数据覆盖）
    applyRecaptureResult({ page, data }) {
      const p = this.pages[page - 1]
      if (!p) return
      const d = data || {}
      p.type = 'sequence'
      p.anim = true
      if (Array.isArray(d.frames)) p.frames = d.frames
      // 封面优先用返回的 img，缺省取首帧（与动画页展示首帧行为一致）
      p.img = d.img || (p.frames && p.frames.length ? p.frames[0] : p.img)
      if (Array.isArray(d.frameT)) p.frameT = d.frameT
      if (d.audio !== undefined) p.audio = d.audio
      if (d.audioT1 !== undefined) p.audioT1 = d.audioT1
      // 视频/旧音频相关字段废弃
      delete p.videoUrl
      delete p.videoBox
      delete p.audios
      delete p.videos
      p._origImg = p.img
      p._replaced = false
      delete p._blank
      this.cur = page - 1
    },
    save() {
      // 剔除内部字段（_origImg/_replaced/_blank），id 按顺序重排（插入空白页后保持 1..N）。
      // 以页面现值为基础：编辑器管理的字段（type/frames/frameT/anim/audio/audioT1/videoUrl/videoBox）覆盖写，
      // 不认识的字段（audios/videos/audioMedia/audioBox/audioLong/audioStart/audioEnd）原样带回，保存不丢
      const pagesJson = JSON.stringify(
        this.pages.map((p, i) => {
          const o = { ...p }
          delete o._origImg
          delete o._replaced
          delete o._blank
          o.id = i + 1
          o.img = p.img
          o.title = p.title || ''
          if (p.type && p.type !== 'image') o.type = p.type; else delete o.type
          if (p.frames && p.frames.length) o.frames = p.frames; else delete o.frames
          if (p.frameT && p.frameT.length) o.frameT = p.frameT; else delete o.frameT
          if (p.anim) o.anim = true; else delete o.anim
          if (p.audio !== undefined && p.audio !== null) o.audio = p.audio; else delete o.audio
          if (p.audioT1 !== undefined && p.audioT1 !== null) o.audioT1 = p.audioT1; else delete o.audioT1
          if (p.videoUrl) o.videoUrl = p.videoUrl; else delete o.videoUrl
          if (p.videoBox) o.videoBox = p.videoBox; else delete o.videoBox
          return o
        })
      )
      this.saving = true
      updatePptProject({
        id: Number(this.id),
        title: this.title,
        themeJson: this.themeJson,
        pagesJson
      })
        .then((res) => {
          if (res && res.data && res.data.code === 0) {
            this.$message.success('保存成功')
            this.goBack()
          } else {
            this.$message.error('保存失败：' + ((res && res.data && res.data.message) || '未知错误'))
          }
        })
        .catch(() => {
          this.$message.error('保存失败，请重试')
        })
        .finally(() => {
          this.saving = false
        })
    },
    goBack() {
      this.$router.push('/pptlist')
    }
  }
}
</script>

<style scoped>
.pptedit-raster {
  height: calc(100vh - 110px);
  display: flex;
  flex-direction: column;
  background: #f4f5f7;
  overflow: hidden;
}
.edit-bar {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 16px;
  background: #fff;
  border-bottom: 1px solid #e4e6eb;
  flex-shrink: 0;
}
.bar-title {
  font-weight: 600;
  font-size: 14px;
}
.bar-info {
  color: #909399;
  font-size: 12px;
  flex: 1;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}
.bar-actions {
  flex-shrink: 0;
}
.edit-body {
  flex: 1;
  display: flex;
  min-height: 0;
}
/* 左侧缩略图 */
.thumbs {
  width: 210px;
  flex-shrink: 0;
  overflow-y: auto;
  background: #fff;
  border-right: 1px solid #e4e6eb;
  padding: 10px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.thumb-item {
  position: relative;
  border: 2px solid transparent;
  border-radius: 4px;
  overflow: hidden;
  cursor: pointer;
  background: #fff;
  flex-shrink: 0;
}
.thumb-item.on {
  border-color: #2f9bff;
}
.thumb-item img {
  display: block;
  width: 100%;
}
.thumb-no {
  position: absolute;
  left: 4px;
  top: 4px;
  background: rgba(0, 0, 0, 0.55);
  color: #fff;
  font-size: 11px;
  padding: 1px 7px;
  border-radius: 8px;
}
.thumb-tag {
  position: absolute;
  right: 4px;
  top: 4px;
  background: #f56c6c;
  color: #fff;
  font-size: 10px;
  padding: 1px 6px;
  border-radius: 8px;
}
.thumb-tag.blank {
  background: #909399;
}
.thumb-del {
  position: absolute;
  left: 2px;
  top: 2px;
  width: 20px;
  height: 20px;
  border: none;
  border-radius: 50%;
  background: rgba(208, 48, 48, 0.88);
  color: #fff;
  font-size: 12px;
  line-height: 20px;
  text-align: center;
  cursor: pointer;
  padding: 0;
  opacity: 0.85;
  z-index: 2;
}
.thumb-del:hover {
  opacity: 1;
  background: #d03050;
}
.empty {
  text-align: center;
  color: #909399;
  padding: 40px 0;
  font-size: 13px;
}
/* 右侧舞台 */
.stage {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
  padding: 14px 18px;
  box-sizing: border-box;
}
.stage-head {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
  flex-shrink: 0;
}
.page-label {
  font-size: 14px;
  font-weight: 600;
}
.replaced-tip {
  color: #f56c6c;
  font-size: 12px;
}
.stage-body {
  flex: 1;
  display: flex;
  min-height: 0;
  background: #e8eaee;
  border-radius: 6px;
  overflow: hidden;
  padding: 14px;
  box-sizing: border-box;
}
.canvas-wrap {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fff;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
  border-radius: 2px;
  overflow: hidden;
}
.page-img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: contain;
}
.stage-tools {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 0 0;
  flex-shrink: 0;
  flex-wrap: wrap;
}
.tool-tip {
  color: #909399;
  font-size: 12px;
}
/* 截图来源选择器 */
.picker-tip {
  font-size: 12px;
  color: #666;
  line-height: 1.8;
  background: #f7f8fa;
  border-radius: 6px;
  padding: 10px 12px;
  margin-bottom: 12px;
}
.picker-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 10px;
}
.picker-item {
  border: 1px solid #dcdfe6;
  border-radius: 6px;
  overflow: hidden;
  background: #fff;
  cursor: pointer;
  transition: border-color 0.15s;
}
.picker-item:hover {
  border-color: #2f9bff;
}
.picker-item img {
  display: block;
  width: 100%;
  aspect-ratio: 16 / 9;
  object-fit: contain;
  background: #fff;
}
.picker-label {
  padding: 6px 8px;
  font-size: 12px;
  color: #333;
  line-height: 1.5;
}
.picker-upload {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 130px;
  border: 1px dashed #409eff;
  background: #f7faff;
  color: #409eff;
}
.picker-upload-plus {
  font-size: 13px;
}
.picker-upload-sub {
  font-size: 11px;
  color: #999;
  margin-top: 4px;
}
/* 动画效果预览 */
.anim-pv {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.anim-stage {
  background: #141824;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 360px;
  padding: 14px;
}
.anim-stage img {
  max-width: 100%;
  max-height: 62vh;
  object-fit: contain;
  background: #fff;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.35);
}
.anim-ctrl {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  flex-wrap: wrap;
}
.anim-progress {
  font-size: 13px;
  color: #666;
}
</style>
